/**
 * client/src/lib/forensic-recovery-lift-challenge.ts
 *
 * #3042 Part B — the ward (requester) leg of the question-gated
 * `forensic_pause_lift` ceremony. Split from forensic-recovery-api.ts to keep
 * that file focused on the attempt lifecycle and this one under the 300-line
 * cap (DEVELOPMENT/code-style.md).
 *
 * Reuses the SAME hashing/binding pipeline `guardian-mutual-challenge.ts`
 * uses for the approve leg (Reuse-First) -- salt + canon-version fold via
 * `buildGuardianAnswerDigests`, then `bindAnswerDigest` under a freshly minted
 * requester-leg nonce (#3029). Raw answers never leave the device (Rule 5).
 */

import { z } from "zod";

import { apiRequest } from "@/lib/queryClient";
import { buildGuardianAnswerDigests } from "@/lib/guardian-answer-digests";
import { bindAnswerDigest } from "@shared/lib/guardian-verification/attempt-binding";
import type { MutualChallengeQuestion } from "@/lib/guardian-mutual-challenge";

export interface WardLiftGuardianQuestions {
  connectionId: string;
  guardianLabel: string;
  questions: MutualChallengeQuestion[];
}

export interface WardLiftQuestionsResponse {
  gated: boolean;
  guardians: WardLiftGuardianQuestions[];
}

/**
 * Wire-shape validation (adversarial review round 1, MINOR 10) — mirrors the
 * `dutiesSchema.parse` pattern in `forensic-recovery-guardian-duties.ts`: a
 * server response that drifts from this shape fails loudly here instead of
 * quietly reaching `QuestionAnswerer` with a wrong/missing field.
 */
const wardLiftQuestionSchema = z.object({
  order: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  text: z.string(),
  salt: z.string(),
  canonVersion: z.union([z.literal(1), z.literal(2)]).optional(),
}).strict();
const wardLiftGuardianQuestionsSchema = z.object({
  connectionId: z.string(),
  guardianLabel: z.string(),
  questions: z.array(wardLiftQuestionSchema),
}).strict();
const wardLiftQuestionsResponseSchema = z.object({
  gated: z.boolean(),
  guardians: z.array(wardLiftGuardianQuestionsSchema),
}).strict();

/** The caller's own read of what a gated lift ceremony would ask of them. */
export async function fetchWardLiftQuestions(): Promise<WardLiftQuestionsResponse> {
  const response = await apiRequest<WardLiftQuestionsResponse>(
    "/api/security/forensic-recovery/mutual-challenge/questions",
  );
  return wardLiftQuestionsResponseSchema.parse(response);
}

/** Mints ONE attempt nonce for the requester leg -- shared across every guardian's proof triple in this attempt. */
export function mintWardLiftChallengeNonce(): Promise<{ nonce: string; expiresAt: string }> {
  return apiRequest<{ nonce: string; expiresAt: string }>(
    "/api/circle/mutual-challenge/nonce",
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ leg: "requester" }) },
  );
}

export interface WardLiftChallenge {
  mutualChallengeAnswers: Record<string, string[]>;
  mutualChallengeNonce: string;
}

/** Turns per-guardian typed answers into the wire map, bound to ONE nonce. */
export function buildWardLiftChallengeAnswers(
  guardians: readonly WardLiftGuardianQuestions[],
  rawAnswersByConnection: Readonly<Record<string, readonly string[]>>,
  nonce: string,
): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const guardian of guardians) {
    const rawAnswers = rawAnswersByConnection[guardian.connectionId];
    if (!rawAnswers) continue;
    const digests = buildGuardianAnswerDigests(
      guardian.questions.map((q) => ({ order: q.order, salt: q.salt, canonVersion: q.canonVersion })),
      rawAnswers,
    );
    map[guardian.connectionId] = digests.map((d) => bindAnswerDigest(nonce, d.order, d.answerHashShake256));
  }
  return map;
}

/** Mint a fresh nonce and build the whole answer map against it, in that order. */
export async function prepareWardLiftChallenge(
  guardians: readonly WardLiftGuardianQuestions[],
  rawAnswersByConnection: Readonly<Record<string, readonly string[]>>,
): Promise<WardLiftChallenge> {
  const { nonce } = await mintWardLiftChallengeNonce();
  return {
    mutualChallengeAnswers: buildWardLiftChallengeAnswers(guardians, rawAnswersByConnection, nonce),
    mutualChallengeNonce: nonce,
  };
}
